import Image from 'next/image';
import type { MeasurementPromptVisual } from '../sdk-adapter.client';
import styles from './FaceWidthMeasurementDialogHost.module.css';

const ASSET_ROOT = '/zeelool-next/lens-measurement/runtime';

function StaticPromptIcon({ src, nodeId }: { src: string; nodeId: string }) {
  return (
    <span className={styles.promptIcon} data-node-id={nodeId} aria-hidden>
      <Image src={`${ASSET_ROOT}/${src}`} alt="" fill sizes="56px" />
    </span>
  );
}

function ArrowPromptIcon({ direction }: { direction: 'up' | 'down' | 'left' | 'right' }) {
  const nodeIds = {
    up: ['5769:1440', '5769:1441'],
    down: ['5769:1443', '5769:1444'],
    left: ['5769:1446', '5769:1447'],
    right: ['5769:1449', '5769:1451'],
  } as const;

  return (
    <span className={styles.promptIcon} data-node-id={nodeIds[direction][0]} aria-hidden>
      <span
        className={`${styles.promptArrowMotion} ${styles[`promptArrow${direction}`]}`}
        data-node-id={nodeIds[direction][1]}
      >
        <span className={`${styles.promptArrowGlyph} ${styles[`promptArrowGlyph${direction}`]}`}>
          <Image src={`${ASSET_ROOT}/move-up.svg`} alt="" fill sizes="25px" />
        </span>
      </span>
    </span>
  );
}

function TurnFacePromptIcon({ direction }: { direction: 'left' | 'right' }) {
  const rootNodeId = direction === 'left' ? '5769:1452' : '5769:1460';
  const correctNodeId = direction === 'left' ? '5769:1454' : '5769:1462';

  return (
    <span
      className={`${styles.promptIcon} ${direction === 'right' ? styles.promptIconMirrorX : ''}`}
      data-node-id={rootNodeId}
      aria-hidden
    >
      <span className={styles.faceOutlineLayer}>
        <Image src={`${ASSET_ROOT}/face-turn-${direction}-outline.svg`} alt="" fill sizes="42px" />
      </span>
      <span className={styles.faceTurnCorrectLayer} data-node-id={correctNodeId}>
        <Image src={`${ASSET_ROOT}/face-turn-${direction}-correct.svg`} alt="" fill sizes="20px" />
      </span>
    </span>
  );
}

function PitchFacePromptIcon({ direction }: { direction: 'up' | 'down' }) {
  const rootNodeId = direction === 'up' ? '5769:1464' : '5769:1467';
  const correctNodeId = direction === 'up' ? '5769:1466' : '5769:1469';

  return (
    <span className={styles.promptIcon} data-node-id={rootNodeId} aria-hidden>
      <span className={styles.faceOutlineLayer}>
        <Image src={`${ASSET_ROOT}/face-${direction}-outline.svg`} alt="" fill sizes="42px" />
      </span>
      <span
        className={`${styles.facePitchCorrectLayer} ${styles[`facePitch${direction}`]}`}
        data-node-id={correctNodeId}
      >
        <span className={`${styles.facePitchScaleLayer} ${styles[`facePitchScale${direction}`]}`}>
          <Image src={`${ASSET_ROOT}/face-${direction}-correct.svg`} alt="" fill sizes="22px" />
        </span>
      </span>
    </span>
  );
}

export default function MeasurementPromptIcon({ visual }: { visual: MeasurementPromptVisual }) {
  switch (visual) {
    case 'move-face-up':
      return <ArrowPromptIcon direction="up" />;
    case 'move-face-down':
      return <ArrowPromptIcon direction="down" />;
    case 'move-face-left':
      return <ArrowPromptIcon direction="left" />;
    case 'move-face-right':
      return <ArrowPromptIcon direction="right" />;
    case 'move-closer':
      return <StaticPromptIcon src="move-closer.svg" nodeId="5769:1336" />;
    case 'move-farther':
      return <StaticPromptIcon src="move-farther.svg" nodeId="5769:1344" />;
    case 'turn-head-left':
      return <TurnFacePromptIcon direction="left" />;
    case 'turn-head-right':
      return <TurnFacePromptIcon direction="right" />;
    case 'raise-chin':
      return <PitchFacePromptIcon direction="up" />;
    case 'lower-chin':
      return <PitchFacePromptIcon direction="down" />;
    case 'place-card':
    case 'invalid-card':
      return <StaticPromptIcon src="card-neutral.svg" nodeId="5769:1437" />;
    case 'level-card':
      return <StaticPromptIcon src="card-tilt.svg" nodeId="5769:1395" />;
    case 'center-face':
    case 'straighten-head':
    case 'measuring':
      return <StaticPromptIcon src="face-neutral.svg" nodeId="5769:1380" />;
  }
}
